import { useRef } from 'react';
import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
} from 'framer-motion';
import {
  Lightbulb,
  Network,
  ShoppingBag,
  Handshake,
  BarChart3,
  Bot
} from 'lucide-react';
import { DURATION, EASE } from '../../animations/timing';
import { useReducedMotion } from '../../animations/timing';

// ─── Default feature card data with Lucide SVG Icons
const DEFAULT_CARDS = [
  {
    id: 'validate',
    icon: Lightbulb,
    title: 'Startup Idea Validation',
    description:
      'Instantly analyze viability, score potential, and run real-time competitor density scans using our specialized AI validation engine.',
    tag: 'AI Powered',
    color: '#C1440E',
  },
  {
    id: 'mindmap',
    icon: Network,
    title: 'Strategic Mind Mapping',
    description:
      'Map out your roadmap, brainstorm architectures, and connect nodes in a collaborative infinite strategy canvas.',
    tag: 'Collaborative',
    color: '#E5622D',
  },
  {
    id: 'marketplace',
    icon: ShoppingBag,
    title: 'Tech Marketplace',
    description:
      'Access deployable code blocks, pre-built landing pages, and verified API configurations curated to skip R&D efforts.',
    tag: 'Curated',
    color: '#F5A623',
  },
  {
    id: 'mentorship',
    icon: Handshake,
    title: 'Investor & Mentor Matching',
    description:
      'Connect directly with vetted startup advisors, domain specialists, and venture capitalists looking to fund matching tech profiles.',
    tag: 'On-Demand',
    color: '#10B981',
  },
  {
    id: 'reports',
    icon: BarChart3,
    title: 'Deep Viability Reports',
    description:
      'Generate TAM/SAM/SOM market analysis, competitor radars, and pitch-ready startup viability booklets in minutes.',
    tag: 'Instant',
    color: '#F59E0B',
  },
  {
    id: 'ai',
    icon: Bot,
    title: '24/7 AI Launch Advisor',
    description:
      'Ask strategic questions about monetization models, pricing structures, legal terms, or scaling techniques with our trained launch pilot.',
    tag: 'Always On',
    color: '#EC4899',
  },
];

// ─────────────────────────────────────────────
// TiltCard — individual card with 3D hover tilt
// ─────────────────────────────────────────────
function TiltCard({ card, index }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-60, 60], [10, -10]);
  const rotateY = useTransform(x, [-80, 80], [-10, 10]);

  const springRotX = useSpring(rotateX, { stiffness: 280, damping: 28 });
  const springRotY = useSpring(rotateY, { stiffness: 280, damping: 28 });

  function onMouseMove(e) {
    if (reduced) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  }

  function onMouseLeave() {
    x.set(0);
    y.set(0);
  }

  const gradientBorder = `linear-gradient(135deg, ${card.color}60, transparent 60%, ${card.color}30)`;
  const IconComponent = card.icon;

  return (
    <motion.div
      ref={ref}
      className="feature-card"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 40, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1], // Expo-out curve
        delay: index * 0.08, // Stagger reveal natively
      }}
      style={{
        rotateX: reduced ? 0 : springRotX,
        rotateY: reduced ? 0 : springRotY,
        transformStyle: 'preserve-3d',
        perspective: 1200,
        position: 'relative',
        height: '100%',
      }}
      whileHover={{ y: -6 }}
    >
      {/* Animated gradient border */}
      <motion.div
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
        style={{
          position: 'absolute',
          inset: -1,
          borderRadius: 17,
          background: gradientBorder,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Card surface */}
      <motion.div
        whileHover={{
          boxShadow: `var(--shadow-lg), 0 0 40px ${card.color}18`,
          borderColor: `${card.color}30`,
        }}
        style={{
          position: 'relative',
          zIndex: 1,
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid var(--border-default)',
          borderRadius: 16,
          padding: '28px 28px 32px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
        }}
      >
        {/* Tag */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 100,
          background: `${card.color}15`,
          border: `1px solid ${card.color}30`,
          marginBottom: 20,
          width: 'fit-content',
        }}>
          <span style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: card.color,
            boxShadow: `0 0 6px ${card.color}`,
          }} />
          <span style={{
            color: card.color,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {card.tag}
          </span>
        </div>

        {/* Premium Icon Container with micro-hover rotation */}
        <motion.div
          whileHover={{
            scale: 1.1,
            rotate: 8,
            backgroundColor: `${card.color}18`,
          }}
          transition={{ duration: 0.2 }}
          style={{
            width: 56,
            height: 56,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 14,
            background: `${card.color}12`,
            border: `1px solid ${card.color}20`,
            color: card.color,
            transformStyle: 'preserve-3d',
            transform: 'translateZ(20px)',
          }}
        >
          {IconComponent && <IconComponent size={28} strokeWidth={2} />}
        </motion.div>

        {/* Text */}
        <h3 style={{
          color: 'var(--text-primary)',
          fontSize: 18,
          fontWeight: 700,
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          marginBottom: 10,
          letterSpacing: '-0.02em',
        }}>
          {card.title}
        </h3>

        <p style={{
          color: 'var(--text-body)',
          fontSize: 14,
          lineHeight: 1.65,
          fontFamily: 'DM Sans, sans-serif',
          margin: '0 0 auto',
        }}>
          {card.description}
        </p>

        {/* Bottom link */}
        <motion.div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 20,
            color: card.color,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'DM Sans, sans-serif',
            cursor: 'pointer',
          }}
          whileHover={{ x: 4 }}
          transition={{ duration: 0.15 }}
        >
          Explore feature <span>→</span>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// FeatureCards Component
// ─────────────────────────────────────────────
export default function FeatureCards({ cards = DEFAULT_CARDS, title, subtitle }) {
  return (
    <section id="features" style={{
      padding: '100px 24px',
      background: 'var(--bg-page)',
      position: 'relative',
    }}>
      {/* Subtle grid pattern overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(193, 68, 14,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(193, 68, 14,0.04) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: DURATION.normal, ease: EASE.spring }}
            style={{
              display: 'inline-block',
              padding: '6px 18px',
              borderRadius: 100,
              background: 'rgba(193, 68, 14,0.08)',
              border: '1px solid rgba(193, 68, 14,0.20)',
              color: '#C1440E',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: 'DM Sans, sans-serif',
              marginBottom: 20,
            }}
          >
            Everything you need
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: DURATION.normal, ease: EASE.spring, delay: 0.1 }}
            style={{
              color: 'var(--text-primary)',
              fontSize: 'clamp(32px, 4vw, 52px)',
              fontWeight: 800,
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              marginBottom: 16,
            }}
          >
            {title || 'One platform, infinite potential'}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: DURATION.normal, ease: EASE.spring, delay: 0.2 }}
            style={{
              color: 'var(--text-body)',
              fontSize: 18,
              fontFamily: 'DM Sans, sans-serif',
              lineHeight: 1.6,
              maxWidth: 520,
              margin: '0 auto',
            }}
          >
            {subtitle || 'AI tools built for founders at every stage, from first spark to scale.'}
          </motion.p>
        </div>

        {/* Cards grid layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {cards.map((card, i) => (
            <TiltCard key={card.id} card={card} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
